import * as React from 'react'
import {IntlShape, useIntl} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {ScoreRatingLarge} from '@vio/atlas/experimental/ScoreRatingLarge'
import {
  BadgeWrapper,
  ScoreRatingSquared
} from '@vio/atlas/experimental/ScoreRatingSquared'
import {ScoreRating, ScoreRatingLoader} from '@vio/atlas/ScoreRating'
import {cssTheme} from '@vio/atlas/src/themes'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {
  getReviewSentimentKey,
  reviewSentimentMessages
} from '@vio/core/src/hotel/reviewSentiments'
import {toOneDecimalOrInteger} from '@vio/core/src/utils/number'

export {ScoreRatingLoader as ReviewRatingLoader}

interface Props {
  rating: number | undefined
  totalRatingCount?: number
  withIcon?: boolean
  dataId?: string
  underlineSubLabel?: boolean
  hotelId?: string
  variant?:
    | 'compact'
    | 'large'
    | 'default'
    | 'simple-default'
    | 'squared'
    | 'simple-squared'
  singleLineLabels?: boolean
}

const DEFAULT_DATA_ID = 'ReviewRating'

const LabelWrapper = styled.span`
  &.--is-underline {
    text-decoration: underline;
  }
`

const SquaredWrapper = styled.div`
  .--simple-squared {
    ${BadgeWrapper} {
      ${cssTheme.typography.labelXS}
      padding: ${cssTheme.layout.spacing.s100} ${cssTheme.layout.spacing.s200};
      min-width: 24px;
    }
  }
`

const getSubLabel = (
  totalRatingCount: Props['totalRatingCount'],
  variant: Props['variant'],
  formatMessage: IntlShape['formatMessage']
) => {
  switch (variant) {
    case 'large':
      if (!totalRatingCount) {
        return formatMessage({
          id: 'reviews.basedOnNoReviews',
          defaultMessage: 'Based on verified reviews'
        })
      }
      return formatMessage(
        {
          id: 'reviews.basedOnReviews',
          defaultMessage:
            '{reviewsCount, plural, one {Based on 1 verified review} other {Based on # verified reviews}}'
        },
        {reviewsCount: totalRatingCount}
      )

    case 'compact':
      if (!totalRatingCount) {
        return formatMessage({
          id: 'noReviews',
          defaultMessage: 'No reviews yet'
        })
      }
      return totalRatingCount?.toLocaleString()

    case 'simple-default':
    case 'simple-squared':
      if (!totalRatingCount) {
        return formatMessage({
          id: 'noReviews',
          defaultMessage: 'No reviews yet'
        })
      }
      return `(${totalRatingCount?.toLocaleString()})`

    default:
      if (!totalRatingCount) {
        return formatMessage({
          id: 'noReviews',
          defaultMessage: 'No reviews yet'
        })
      }
      return formatMessage(
        {
          id: 'reviewsCount',
          defaultMessage: '{reviews, plural, one {# review} other {# reviews}}'
        },
        {reviews: totalRatingCount}
      )
  }
}

export const ReviewRating = ({
  rating,
  totalRatingCount,
  underlineSubLabel = false,
  dataId = DEFAULT_DATA_ID,
  variant = 'default',
  singleLineLabels = false,
  hotelId
}: Props) => {
  const {formatMessage} = useIntl()
  const toggle = useToggle()

  const isNoReviewsBSide = toggle('8783dc23-no-reviews', false, true)

  if (rating == null) return null

  const sentimentMessageKey = getReviewSentimentKey(rating)
  const translatedSentiment = sentimentMessageKey
    ? reviewSentimentMessages[sentimentMessageKey]
    : null

  const formattedRating = toOneDecimalOrInteger(rating)

  if (formattedRating === null && !isNoReviewsBSide) {
    return null
  }

  const subLabel = getSubLabel(totalRatingCount, variant, formatMessage)

  if (variant === 'squared' || variant === 'simple-squared') {
    return (
      <SquaredWrapper data-id={dataId}>
        <ScoreRatingSquared
          className={cx(variant === 'simple-squared' && '--simple-squared')}
          hasPlainSubLabel={variant === 'simple-squared'}
          value={formattedRating ?? 0}
          label={translatedSentiment && formatMessage(translatedSentiment)}
          subLabel={subLabel}
          scoreId={hotelId && `HotelCardMobileScoreValue-${hotelId}`}
          scoreLabelId={hotelId && `HotelCardMobileScoreLabel-${hotelId}`}
          scoreSubLabelId={hotelId && `HotelCardMobileScoreSubLabel-${hotelId}`}
        />
      </SquaredWrapper>
    )
  }

  if (variant === 'large') {
    return (
      <div data-id={dataId}>
        <ScoreRatingLarge
          value={formattedRating ?? 0}
          label={translatedSentiment && formatMessage(translatedSentiment)}
          subLabel={
            subLabel && (
              <LabelWrapper
                className={cx(underlineSubLabel && '--is-underline')}
              >
                {subLabel}
              </LabelWrapper>
            )
          }
          scoreId={hotelId && `HotelCardMobileScoreValue-${hotelId}`}
          scoreLabelId={hotelId && `HotelCardMobileScoreLabel-${hotelId}`}
          scoreSubLabelId={hotelId && `HotelCardMobileScoreSubLabel-${hotelId}`}
        />
      </div>
    )
  }

  return (
    <div data-id={dataId}>
      <ScoreRating
        value={formattedRating ?? 0}
        label={translatedSentiment && formatMessage(translatedSentiment)}
        singleLineLabels={singleLineLabels}
        subLabel={
          subLabel && (
            <LabelWrapper className={cx(underlineSubLabel && '--is-underline')}>
              {subLabel}
            </LabelWrapper>
          )
        }
        isCompact={variant === 'compact'}
        scoreId={hotelId && `HotelCardMobileScoreValue-${hotelId}`}
        scoreLabelId={hotelId && `HotelCardMobileScoreLabel-${hotelId}`}
        scoreSubLabelId={hotelId && `HotelCardMobileScoreSubLabel-${hotelId}`}
      />
    </div>
  )
}
